import React from 'react';
import { DataGrid, GridColDef, gridClasses, deDE } from '@mui/x-data-grid';

import { observer, useLocalObservable } from 'mobx-react-lite';
import { Box, alpha, styled } from '@mui/material';
import moment from 'moment';
import { numberFormatterInEuro } from '../../utils';
import { useStore } from '../../store/context';

const ODD_OPACITY = 0.1;

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  borderRadius: '3px',
  borderWidth: 0,
  borderColor: theme.palette.background.paper,
  backgroundColor: theme.palette.secondary.light,

  '& .MuiDataGrid-columnHeaders': {
    fontSize: 16,
    color: theme.palette.secondary.dark,
  },

  // customise cell borders
  '& .MuiDataGrid-cell': {
    borderLeft: `3px solid ${theme.palette.divider}`,
    borderTop: `none`,
    borderBottom: `none`,
  },

  // remove left border from left column
  [`& .${gridClasses.cell}.noLeftBorder`]: {
    borderLeft: 'none',
  },

  // change background color of even rows
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.background.paper,
    '&:hover, &.Mui-hovered': {
      backgroundColor: alpha(theme.palette.secondary.dark, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  // change background color of odd rows
  [`& .${gridClasses.row}.odd`]: {
    '&:hover, &.Mui-hovered': {
      backgroundColor: alpha(theme.palette.secondary.dark, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
}));

const OilPricesTable = observer(() => {
  const { welcomeStore } = useStore();
  const welcomeData = welcomeStore.getWelcomeData();

  const columnWidth = 800;
  const columnMinWidth = 100;

  const columns: GridColDef[] = [
    {
      field: 'price',
      headerName: '10001 bis 30000 L',
      flex: 1,
      maxWidth: columnWidth,
      minWidth: columnMinWidth,
      editable: false,
      headerAlign: 'center',
      align: 'center',
      cellClassName: 'noLeftBorder',
      valueFormatter: params => numberFormatterInEuro(params.value),
    },
    {
      field: 'priceMediumQuantity',
      headerName: ' 5001 bis 10000 L',
      flex: 1,
      maxWidth: columnWidth,
      minWidth: columnMinWidth,
      editable: false,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: params => numberFormatterInEuro(params.value),
    },
    {
      field: 'priceMinQuantity',
      headerName: 'bis 5000 L',
      flex: 1,
      maxWidth: columnWidth,
      minWidth: columnMinWidth,
      editable: false,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: params => numberFormatterInEuro(params.value),
    },
    {
      field: 'abfuellpauschale',
      headerName: 'Abfüllpauschale',
      flex: 1,
      maxWidth: columnWidth,
      minWidth: columnMinWidth,
      editable: false,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: params => numberFormatterInEuro(params.value),
    },
    {
      field: 'date',
      headerName: 'Datum',
      sortable: false,
      flex: 1,
      maxWidth: columnWidth,
      minWidth: columnMinWidth,
      valueFormatter: params => moment(params?.value).format('DD.MM.YYYY'),
      headerAlign: 'center',
      align: 'center',
    },
  ];

  const localState = useLocalObservable(() => ({
    get oilPrices() {
      if (welcomeData?.oilPrices) {
        return welcomeData?.oilPrices.map((item, index) => ({
          ...item,
          id: index + 1,
        }));
      }
      return [];
    },
  }));

  return (
    <Box width="100%" marginTop={3} id="datagrid">
      {welcomeData?.oilPrices && welcomeData.oilPrices.length > 0 ? (
        <StripedDataGrid
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
          rows={localState.oilPrices}
          columns={columns}
          disableRowSelectionOnClick
          getRowClassName={params =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
        />
      ) : (
        <div>Keine Daten derzeit verfügbar</div>
      )}
    </Box>
  );
});

export default OilPricesTable;
